import { createTheme, type ThemeOptions } from '@mui/material/styles';

/**
 * Application color palette - Based on Numen DS
 */
export const appColors = {
    primary: {
        main: '#F39323',
        light: '#F5A854',
        dark: '#E07D0F',
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: '#E84B4C',
        light: '#EE7677',
        dark: '#DC2829',
        contrastText: '#FFFFFF',
    },
    accent: {
        main: '#E07D0F',
        light: '#FFB042',
        dark: '#AD4A00',
    },
    error: {
        main: '#F87171',
        light: '#FCA5A5',
        dark: '#DC2626',
    },
    warning: {
        main: '#F59E0B',
        light: '#FCD34D',
        dark: '#D97706',
    },
    info: {
        main: '#3B82F6',
        light: '#93C5FD',
        dark: '#1D4ED8',
    },
    success: {
        main: '#10B981',
        light: '#6EE7B7',
        dark: '#065F46',
    },
    grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
    },
};

/** Numen gradient */
export const gradients = {
    primary: 'linear-gradient(135deg, #F39323 0%, #E84B4C 100%)',
    primaryHover: 'linear-gradient(135deg, #E07D0F 0%, #DC2829 100%)',
    dark: 'linear-gradient(135deg, #11132B 0%, #252851 100%)',
    darkSidebar: 'linear-gradient(180deg, #11132B 0%, #1A1C3E 100%)',
    hero: 'linear-gradient(135deg, #F39323 0%, #E84B4C 50%, #F5A854 100%)',
};

const baseThemeOptions: ThemeOptions = {
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
        h1: { fontWeight: 800, letterSpacing: '-0.02em' },
        h2: { fontWeight: 700, letterSpacing: '-0.01em' },
        h3: { fontWeight: 700 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
    },
    spacing: 8,
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: '10px 24px',
                    fontWeight: 600,
                },
                containedPrimary: {
                    background: gradients.primary,
                    '&:hover': {
                        background: gradients.primaryHover,
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 10,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
            },
        },
    },
};

/**
 * Light theme
 */
export const lightTheme = createTheme({
    ...baseThemeOptions,
    palette: {
        mode: 'light',
        primary: appColors.primary,
        secondary: appColors.secondary,
        error: appColors.error,
        warning: appColors.warning,
        info: appColors.info,
        success: appColors.success,
        background: {
            default: '#FAFBFC',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1F2937',
            secondary: '#6B7280',
        },
        divider: '#E5E7EB',
    },
});

/**
 * Dark theme - Numen DS dark palette
 */
export const darkTheme = createTheme({
    ...baseThemeOptions,
    palette: {
        mode: 'dark',
        primary: {
            main: '#F5A854',
            light: '#F8BD7A',
            dark: '#F39323',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#EE7677',
            light: '#F29A9B',
            dark: '#E84B4C',
            contrastText: '#FFFFFF',
        },
        error: {
            main: '#FCA5A5',
            light: '#FECACA',
            dark: '#F87171',
        },
        warning: {
            main: '#FCD34D',
            light: '#FDE68A',
            dark: '#F59E0B',
        },
        info: {
            main: '#93C5FD',
            light: '#BFDBFE',
            dark: '#3B82F6',
        },
        success: {
            main: '#6EE7B7',
            light: '#A7F3D0',
            dark: '#10B981',
        },
        background: {
            default: '#11132B',
            paper: '#1A1C3E',
        },
        text: {
            primary: '#F8FAFC',
            secondary: '#CBD5E1',
        },
        divider: '#252851',
    },
    components: {
        ...baseThemeOptions.components,
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.06)',
                },
            },
        },
    },
});

export default lightTheme;
